/**
 * FlashBook theme selector — lists FlashBook default themes + FlashBook HTML
 * bridge themes (separate from capsule themes). Reused inline inside the
 * FlashBookStudio as an overlay (hideHeader) or as a standalone screen.
 *
 * A creator is not choosing a colour scheme, they are choosing what their book
 * will feel like, so a default theme is presented as a *miniature page* built
 * from the same pieces the reader uses (see `FlashBookThemePreview`) rather than
 * a decorative swatch. The theme's intent metadata sits beside it, and the list
 * can be narrowed by category.
 */
import {useMemo, useState} from 'react'
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {type CommonNavigatorParams} from '#/lib/routes/types'
import {FlashBookThemePreview} from '#/components/flashbooks/FlashBookThemePreview'
import {listFlashbookThemeItems} from '#/themes-flashbook/registry'
import {readableAccent, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {type FlashBookThemeItem} from '#/types/flashbook'

type Props = NativeStackScreenProps<
  CommonNavigatorParams,
  'FlashBookThemeSelector'
>

export function FlashBookThemeSelectorScreen({navigation}: Props) {
  return (
    <FlashBookThemeSelectorView
      onBack={() => navigation.goBack()}
      onSelect={themeId => {
        navigation.replace('FlashBookStudio', {themeId})
      }}
    />
  )
}

export function FlashBookThemeSelectorView({
  hideHeader = false,
  onBack,
  onSelect,
  showSafeAreaTop = true,
}: {
  hideHeader?: boolean
  onBack: () => void
  onSelect: (themeId: string, htmlThemeUrl?: string) => void
  showSafeAreaTop?: boolean
}) {
  const ss = useStyles()
  const c = useStudioChrome()
  const {t: l} = useLingui()
  const insets = useSafeAreaInsets()
  const items = useMemo(() => listFlashbookThemeItems(), [])
  const nativeItems = items.filter(i => i.kind === 'native')
  const htmlItems = items.filter(i => i.kind === 'html')
  const [tab, setTab] = useState<'default' | 'html'>('default')
  const [category, setCategory] = useState<string>('all')

  const tabItems = tab === 'default' ? nativeItems : htmlItems

  // Only offer a filter when there is actually something to filter — a single
  // chip row over one category would just be noise.
  const categories = useMemo(
    () => Array.from(new Set(tabItems.map(i => i.category))),
    [tabItems],
  )
  const list =
    category === 'all'
      ? tabItems
      : tabItems.filter(i => i.category === category)

  const categoryLabel = (c: string) => {
    switch (c) {
      case 'stories':
        return l`Stories`
      case 'education':
        return l`Education`
      case 'editorial':
        return l`Editorial`
      case 'kids':
        return l`Kids & games`
      case 'classic':
        return l`Classic`
      case 'minimal':
        return l`Minimal`
      case 'missions':
        return l`Missions`
      case 'science':
        return l`Science`
      case 'maths':
        return l`Maths`
      case 'comics':
        return l`Comics`
      case 'culture':
        return l`Culture`
      default:
        return c
    }
  }

  const switchTab = (next: 'default' | 'html') => {
    setTab(next)
    setCategory('all')
  }

  return (
    <View style={[ss.root, {paddingTop: showSafeAreaTop ? insets.top : 0}]}>
      {!hideHeader && (
        <View style={ss.header}>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Back`}
            accessibilityHint={l`Returns to the cover step`}
            onPress={onBack}
            style={ss.backBtn}>
            <Feather name="arrow-left" size={20} color={c.text} />
          </TouchableOpacity>
          <Text style={ss.headerTitle}>{l`Choose a FlashBook theme`}</Text>
        </View>
      )}

      {/* Tabs */}
      <View style={ss.tabs}>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={() => switchTab('default')}
          style={[ss.tab, tab === 'default' && ss.tabActive]}>
          <Text style={[ss.tabTxt, tab === 'default' && ss.tabTxtActive]}>
            {l`Default themes`}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={() => switchTab('html')}
          style={[ss.tab, tab === 'html' && ss.tabActive]}>
          <Text style={[ss.tabTxt, tab === 'html' && ss.tabTxtActive]}>
            {l`HTML themes`}
          </Text>
        </TouchableOpacity>
      </View>

      {categories.length > 1 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={ss.filters}>
          <FilterChip
            label={l`All`}
            active={category === 'all'}
            onPress={() => setCategory('all')}
          />
          {categories.map(c => (
            <FilterChip
              key={c}
              label={categoryLabel(c)}
              active={category === c}
              onPress={() => setCategory(c)}
            />
          ))}
        </ScrollView>
      )}

      <ScrollView
        contentContainerStyle={ss.list}
        showsVerticalScrollIndicator={false}>
        {list.map(item => (
          <ThemeCard
            key={item.id}
            item={item}
            onSelect={onSelect}
            categoryLabel={categoryLabel(item.category)}
          />
        ))}
        {list.length === 0 && (
          <Text style={ss.empty}>
            {l`No themes here yet — check back soon.`}
          </Text>
        )}
      </ScrollView>
    </View>
  )
}

function FilterChip({
  label,
  active,
  onPress,
}: {
  label: string
  active: boolean
  onPress: () => void
}) {
  const ss = useStyles()
  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityState={{selected: active}}
      onPress={onPress}
      style={[ss.chip, active && ss.chipActive]}>
      <Text style={[ss.chipTxt, active && ss.chipTxtActive]}>{label}</Text>
    </TouchableOpacity>
  )
}

function ThemeCard({
  item,
  onSelect,
  categoryLabel,
}: {
  item: FlashBookThemeItem
  onSelect: (themeId: string, htmlThemeUrl?: string) => void
  categoryLabel: string
}) {
  const ss = useStyles()
  const c = useStudioChrome()
  const {t: l} = useLingui()
  const isNative = item.kind === 'native'
  /*
   * Every use of the theme's colour here is *ink* — a border, a label, an icon —
   * so it goes through `readableAccent` first. A category colour is chosen to
   * look right on its own preview, and several of them (the bridge's teal, the
   * heritage brown, the comic red) are tuned for a dark card and wash out on a
   * light sheet, where this list is now read half the time.
   */
  const accent = readableAccent(item.previewColor, c)

  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={l`${item.name} theme`}
      accessibilityHint={item.description}
      onPress={() =>
        onSelect(item.id, item.kind === 'html' ? item.htmlPath : undefined)
      }
      style={[ss.card, {borderColor: accent + '44'}]}>
      {/* A miniature of the actual book page, not a colour swatch. */}
      <View style={[ss.preview, {borderColor: accent + '3a'}]}>
        {isNative ? (
          <FlashBookThemePreview themeId={item.id} />
        ) : (
          <View style={[ss.emojiThumb, {backgroundColor: accent + '1f'}]}>
            <Text style={ss.thumbEmoji}>{item.emoji ?? '📖'}</Text>
          </View>
        )}
      </View>

      <View style={ss.cardBody}>
        <View style={ss.nameRow}>
          <Text style={ss.cardName} numberOfLines={1}>
            {item.name}
          </Text>
          {item.kind === 'html' && (
            <View style={[ss.htmlBadge, {borderColor: accent + '55'}]}>
              <Text style={[ss.htmlBadgeTxt, {color: accent}]}>
                {l`HTML bridge`}
              </Text>
            </View>
          )}
        </View>

        <Text style={ss.cardDesc}>{item.description}</Text>

        {item.meta && (
          <>
            <View style={ss.metaRow}>
              <Feather
                name="compass"
                size={11}
                color={accent}
                style={ss.metaIcon}
              />
              <Text style={ss.metaTxt} numberOfLines={2}>
                {item.meta.recommendedFor}
              </Text>
            </View>
            <View style={ss.tagRow}>
              <View style={[ss.tag, {borderColor: accent + '55'}]}>
                <Text style={[ss.tagTxt, {color: accent}]}>
                  {item.meta.layoutMode}
                </Text>
              </View>
            </View>
          </>
        )}

        {!item.meta && (
          <View style={ss.tagRow}>
            <View style={[ss.tag, {borderColor: accent + '55'}]}>
              <Text style={[ss.tagTxt, {color: accent}]}>{categoryLabel}</Text>
            </View>
          </View>
        )}
      </View>
    </TouchableOpacity>
  )
}

const makeStyles = (c: StudioChrome) =>
  StyleSheet.create({
    root: {flex: 1, backgroundColor: c.frame},
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      paddingHorizontal: 16,
      paddingVertical: 14,
    },
    backBtn: {
      width: 34,
      height: 34,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
      alignItems: 'center',
      justifyContent: 'center',
    },
    headerTitle: {color: c.text, fontSize: 16, fontWeight: '800'},
    tabs: {
      flexDirection: 'row',
      gap: 8,
      paddingHorizontal: 16,
      marginBottom: 10,
    },
    tab: {
      paddingHorizontal: 14,
      paddingVertical: 7,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: c.borderStrong,
    },
    tabActive: {backgroundColor: c.accentTint, borderColor: c.accentBorder},
    tabTxt: {color: c.textMuted, fontSize: 13, fontWeight: '600'},
    tabTxtActive: {color: c.accent, fontWeight: '800'},
    filters: {
      paddingHorizontal: 16,
      gap: 6,
      paddingBottom: 4,
      alignItems: 'center',
    },
    chip: {
      paddingHorizontal: 11,
      paddingVertical: 5,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    chipActive: {backgroundColor: c.fillStrong, borderColor: c.borderStrong},
    chipTxt: {color: c.textMuted, fontSize: 11.5, fontWeight: '600'},
    chipTxtActive: {color: c.text, fontWeight: '800'},
    list: {padding: 16, gap: 12, paddingBottom: 48},
    card: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
      padding: 12,
      borderRadius: 16,
      borderWidth: 1,
      backgroundColor: c.surface,
    },
    // Portrait, because a FlashBook page is portrait — the preview should read as
    // the page the creator is actually about to make.
    preview: {
      width: 96,
      height: 154,
      borderRadius: 8,
      borderWidth: 1,
      overflow: 'hidden',
      backgroundColor: c.fill,
    },
    emojiThumb: {flex: 1, alignItems: 'center', justifyContent: 'center'},
    thumbEmoji: {fontSize: 30},
    cardBody: {flex: 1, gap: 4, paddingTop: 1},
    nameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexWrap: 'wrap',
    },
    cardName: {color: c.text, fontSize: 15, fontWeight: '700'},
    cardDesc: {color: c.textMuted, fontSize: 12, lineHeight: 17},
    metaRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 5,
      marginTop: 2,
    },
    metaIcon: {marginTop: 2},
    metaTxt: {
      flex: 1,
      color: c.textMuted,
      fontSize: 11.5,
      lineHeight: 16,
      fontWeight: '600',
    },
    tagRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 3},
    tag: {
      borderWidth: 1,
      borderRadius: 10,
      paddingHorizontal: 8,
      paddingVertical: 3,
      maxWidth: '100%',
    },
    tagTxt: {fontSize: 10, fontWeight: '700'},
    htmlBadge: {
      borderWidth: 1,
      borderRadius: 10,
      paddingHorizontal: 7,
      paddingVertical: 2,
    },
    htmlBadgeTxt: {fontSize: 10, fontWeight: '700'},
    empty: {color: c.textFaint, textAlign: 'center', marginTop: 40},
  })

/** The selector's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
